import {
  ActionIcon,
  Box,
  FileButton,
  Image,
  Loader,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPhoto, IconTrash, IconUpload } from "@tabler/icons-react";
import { FC, useEffect, useRef, useState } from "react";
import { useStorageFileUrl } from "utils/hooks/request/useStorageFileUrl";
import {
  PRODUCT_IMAGE_ACCEPT,
  validateProductImageFile,
} from "pages/Product/config";
import { useNotification } from "utils/hooks";
import classes from "./ProductImageInput.module.css";

interface ProductImageInputProps {
  currentImageKey?: string | null;
  selectedFile?: File | null;
  onFileChange: (file: File | null) => void;
}

const ProductImageInput: FC<ProductImageInputProps> = ({
  currentImageKey,
  selectedFile,
  onFileChange,
}) => {
  const { notification } = useNotification();
  const { url: storageUrl, isLoading: isStorageLoading } = useStorageFileUrl(
    selectedFile ? null : currentImageKey,
  );
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);

  useEffect(() => {
    if (!selectedFile) {
      setLocalPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(selectedFile);
    setLocalPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [selectedFile]);

  const displayUrl = localPreviewUrl ?? storageUrl;
  const hasImage = !!displayUrl;

  const handleFileChange = (file: File | null) => {
    if (!file) return;
    const error = validateProductImageFile(file);
    if (error) {
      notification.error({ title: error });
      resetRef.current?.();
      return;
    }
    onFileChange(file);
  };

  const handleRemove = () => {
    onFileChange(null);
    resetRef.current?.();
  };

  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        Ảnh sản phẩm
      </Text>
      <Box className={classes.wrapper}>
        {isStorageLoading && !localPreviewUrl ? (
          <Skeleton className={classes.thumbnail} />
        ) : hasImage ? (
          <Image
            src={displayUrl!}
            className={`${classes.thumbnail} ${classes.clickable}`}
            fit="contain"
            fallbackSrc={undefined}
            onClick={() =>
              modals.open({
                title: "Ảnh sản phẩm",
                size: "xl",
                centered: true,
                children: <Image src={displayUrl!} fit="contain" mah="75vh" />,
              })
            }
          />
        ) : (
          <Box className={`${classes.thumbnail} ${classes.placeholder}`}>
            <IconPhoto size={32} color="var(--mantine-color-gray-5)" />
            <Text size="xs" c="dimmed">
              Chưa có ảnh
            </Text>
          </Box>
        )}

        <Box className={classes.actions}>
          <FileButton
            resetRef={resetRef}
            onChange={handleFileChange}
            accept={PRODUCT_IMAGE_ACCEPT}
          >
            {(props) => (
              <Tooltip label="Chọn ảnh">
                <ActionIcon {...props} variant="filled" size="sm">
                  <IconUpload size={14} />
                </ActionIcon>
              </Tooltip>
            )}
          </FileButton>

          {(hasImage || selectedFile) && (
            <Tooltip label="Xoá ảnh">
              <ActionIcon
                variant="filled"
                color="red"
                size="sm"
                onClick={handleRemove}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Box>

        {isStorageLoading && !localPreviewUrl && (
          <Box className={classes.loaderOverlay}>
            <Loader size="sm" />
          </Box>
        )}
      </Box>
      <Text size="xs" c="dimmed">
        PNG, JPEG, WebP – tối đa 5MB
      </Text>

    </Stack>
  );
};

export default ProductImageInput;
